import React, { useMemo, useState, useRef, useEffect, useCallback, FC } from 'react';
import { D3geoMap, PaperWrapper } from 'components';
import { geoMapData } from 'Redux/geoMapSlice';
import { useSelector } from 'react-redux';
import DMAsPath from '../D3geoMap/JSONmaps/dma_all_paths_latest.json';
import RegionsMap from '../D3geoMap/JSONmaps/usa_regions_paths.json';
import { SegmentSelect, MapSelectionsType, wppSize, normalizeString } from 'utils';
import {
    WppActionButton,
    WppTypography,
    WppIconCross,
    WppSegmentedControl,
    WppSegmentedControlItem,
    WppSelect,
    WppListItem,
} from '@wppopen/components-library-react';
import { useOnClickOutside } from 'hooks/useOnClickOutside/useOnClickOutside';
import styles from './InteractiveMap.module.scss';

const {
    Main,
    MainCard,
    MainCardMapContainer,
    MainCardHeader,
    MainCardFooter,
    MainCardSelection,
    MainCardSelectionDropdown,
    MainCardSelectionItem,
    MainCardHeaderCross,
    MainDropdownWrapper,
} = styles;

export interface InteractiveMapProps {
    disabled?: boolean;
    handleData: (dmaData: any, regionData: any, currentSegment: any) => void;
    currentSelection: any;
    withApplyButton: boolean;
    wppSize: wppSize;
    dataTestId: string;
}

export const InteractiveMap: FC<InteractiveMapProps> = ({
    currentSelection,
    handleData,
    disabled = false,
    withApplyButton = true,
    wppSize = 's',
    dataTestId,
}) => {
    const { permissions } = useSelector(geoMapData);
    const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
    const [dmaData, setDmaData] = useState(currentSelection.DMA);
    const [regionData, setRegionData] = useState(currentSelection.REGION);
    const [currentSegment, setCurrentSegment] = useState(currentSelection.Segment);
    const ref = useRef(null);

    const handleTrigger = useCallback(() => {
        setIsPopupVisible(false);
        !withApplyButton && handleData(dmaData, regionData, currentSegment);
    }, [withApplyButton, dmaData, regionData, currentSegment, handleData]);

    const outsideClick = () => {
        isPopupVisible && handleTrigger();
    };

    const handleRegions = (value: any, type: MapSelectionsType) => {
        setRegionData((prevState: any) => {
            if (type === MapSelectionsType.INTERACTIVE) {
                const isAlreadyExisting = prevState.includes(value);
                const filteredRegions =
                    prevState?.length > 1
                        ? [...prevState.filter((e: any) => e !== value)]
                        : prevState;
                return isAlreadyExisting ? filteredRegions : [...prevState, value];
            }
            if (type === MapSelectionsType.STATIC) {
                return [...value];
            }
        });
    };

    const handleDMA = (value: any) => {
        setDmaData(value);
    };

    const handlePopup = useCallback(() => {
        isPopupVisible && handleTrigger();
        setIsPopupVisible(!isPopupVisible);
    }, [isPopupVisible, handleTrigger]);

    const handleApply = () => {
        handlePopup();
        handleData(dmaData, regionData, currentSegment);
    };

    const handleSegment = (value: SegmentSelect) => {
        setCurrentSegment(value);
    };

    useOnClickOutside(ref, outsideClick);

    const GeoMap = useMemo(
        () => (
            <div className={MainCardMapContainer}>
                {currentSegment === 'REGION' ? (
                    <D3geoMap
                        selectedItems={regionData}
                        handleChange={(value) =>
                            handleRegions(value, MapSelectionsType.INTERACTIVE)
                        }
                        geojsonMap={RegionsMap}
                        permissions={permissions.REGION}
                        filterBy={currentSegment}
                        dataTestId={`${dataTestId}.${currentSegment}.GRAPH`}
                    />
                ) : (
                    <D3geoMap
                        selectedItems={[dmaData]}
                        handleChange={handleDMA}
                        geojsonMap={DMAsPath}
                        permissions={permissions.DMA}
                        filterBy={currentSegment}
                        dataTestId={`${dataTestId}.${currentSegment}.GRAPH`}
                    />
                )}
            </div>
        ),
        [currentSegment, dmaData, permissions.DMA, regionData, permissions.REGION, dataTestId],
    );

    const SelectType = useMemo(() => {
        return currentSegment === 'REGION' ? (
            <WppSelect
                onWppChange={(e) => handleRegions(e.detail.value, MapSelectionsType.STATIC)}
                placeholder="Make selection"
                value={regionData}
                type="multiple"
                key="regionsSelection"
                size={wppSize}
                className={MainCardSelectionDropdown}
                dropdownPosition="absolute"
                locales={{
                    allSelectedText: 'All Geo Regions',
                }}
                data-testid={`${dataTestId}.SUBDROPDOWN`}
            >
                {permissions.REGION.map((el: string) => {
                    const blockedLastElement = regionData.length === 1 && el === regionData[0];
                    return (
                        <WppListItem
                            disabled={blockedLastElement}
                            key={el}
                            value={el}
                            id={el}
                            multiple={true}
                            data-testid={`${dataTestId}.SUBDROPDOWN.${normalizeString(el)}`}
                        >
                            <p slot="label">{el}</p>
                        </WppListItem>
                    );
                })}
            </WppSelect>
        ) : (
            <WppSelect
                onWppChange={(e) => handleDMA(e.detail.value)}
                placeholder="Make selection"
                value={dmaData}
                key="dmasSelection"
                size={wppSize}
                className={MainCardSelectionDropdown}
                dropdownPosition="absolute"
                data-testid={`${dataTestId}.SUBDROPDOWN`}
            >
                {permissions.DMA.map((el: string) => (
                    <WppListItem
                        key={el}
                        value={el}
                        id={el}
                        data-testid={`${dataTestId}.SUBDROPDOWN.${normalizeString(el)}`}
                    >
                        <p slot="label">{el}</p>
                    </WppListItem>
                ))}
            </WppSelect>
        );
    }, [
        permissions.DMA,
        permissions.REGION,
        currentSegment,
        dmaData,
        regionData,
        wppSize,
        dataTestId,
    ]);

    const MainDropdownComponent = useMemo(() => {
        return currentSegment === 'REGION' ? (
            <WppSelect
                value={regionData}
                placeholder="Make selection"
                type="multiple"
                key="regionsSelection2"
                size={wppSize}
                className={MainCardSelectionDropdown}
                locales={{
                    allSelectedText: 'All Geo Regions',
                }}
                dropdownPosition="absolute"
                disabled={disabled}
                style={{ pointerEvents: 'none' }}
                data-testid={`${dataTestId}.DROPDOWN`}
            >
                {permissions.REGION.map((el: string) => (
                    <WppListItem
                        style={{ display: 'none' }}
                        key={el}
                        value={el}
                        id={el}
                        multiple={true}
                        data-testid={`${dataTestId}.DROPDOWN.${normalizeString(el)}`}
                    >
                        <p slot="label" className={MainCardSelectionItem}>
                            {el}
                        </p>
                    </WppListItem>
                ))}
            </WppSelect>
        ) : (
            <WppSelect
                value={dmaData}
                placeholder="Make selection"
                key="dmasSelection"
                size={wppSize}
                className={MainCardSelectionDropdown}
                disabled={disabled}
                style={{ pointerEvents: 'none' }}
                dropdownPosition="absolute"
                data-testid={`${dataTestId}.DROPDOWN`}
            >
                {permissions.DMA.map((el: string) => (
                    <WppListItem
                        key={el}
                        value={el}
                        id={el}
                        data-testid={`${dataTestId}.DROPDOWN.${normalizeString(el)}`}
                    >
                        <p slot="label">{el}</p>
                    </WppListItem>
                ))}
            </WppSelect>
        );
    }, [
        regionData,
        permissions.REGION,
        currentSegment,
        dmaData,
        permissions.DMA,
        disabled,
        wppSize,
        dataTestId,
    ]);

    useEffect(() => {
        const updateStateToDefault = () => {
            setDmaData(currentSelection.DMA);
            setRegionData(currentSelection.REGION);
            setCurrentSegment(currentSelection.Segment);
        };

        !isPopupVisible && updateStateToDefault();
    }, [isPopupVisible, currentSelection]);

    return (
        <div className={Main} ref={ref}>
            {isPopupVisible && (
                <PaperWrapper
                    className={MainCard}
                    backgroundColor="white"
                    data-testid={`${dataTestId}.POPUP.CONTAINER`}
                >
                    <div slot="header" className={MainCardHeader}>
                        <WppTypography type="m-strong" data-testid={`${dataTestId}.POPUP.TITLE`}>
                            Geography
                        </WppTypography>
                        <WppIconCross
                            className={MainCardHeaderCross}
                            onClick={handlePopup}
                            data-testid={`${dataTestId}.POPUP.CLOSE`}
                        ></WppIconCross>
                    </div>

                    <div className={MainCardSelection}>
                        <WppSegmentedControl
                            size={wppSize}
                            value={currentSegment}
                            onWppChange={(e: any) => handleSegment(e.detail.value)}
                            data-testid={`${dataTestId}.POPUP.SEGMENT`}
                        >
                            <WppSegmentedControlItem
                                key="Regions"
                                itemID="Regions"
                                value="REGION"
                                data-testid={`${dataTestId}.POPUP.SEGMENT.REGION`}
                            >
                                Regions
                            </WppSegmentedControlItem>
                            <WppSegmentedControlItem
                                key="DMAs"
                                itemID="DMAs"
                                value="DMA"
                                data-testid={`${dataTestId}.POPUP.SEGMENT.DMA`}
                            >
                                DMAs
                            </WppSegmentedControlItem>
                        </WppSegmentedControl>
                        {SelectType}
                    </div>
                    {GeoMap}
                    {withApplyButton && (
                        <div className={MainCardFooter}>
                            <WppActionButton onClick={handleApply}>Apply</WppActionButton>
                        </div>
                    )}
                </PaperWrapper>
            )}
            <div onClick={() => handlePopup()} className={MainDropdownWrapper}>
                {MainDropdownComponent}
            </div>
        </div>
    );
};
